<template>
  <!-- eslint-disable tailwindcss/no-custom-classname -->
  <!-- eslint-disable-next-line vuejs-accessibility/no-static-element-interactions -->
  <div
    ref="rowRef"
    :[SCROLL_CONTAINER_ATTRIBUTE]="''"
    :aria-label="category.title"
    :class="[
      'marquee-row -mx-16 overflow-hidden scrollbar-none',
      isDragging ? 'is-dragging' : '',
    ]"
    :style="{ '--marquee-duration': `${durationMs}ms` }"
    tabindex="-1"
    @pointerdown="onPointerDown"
    @pointerenter="onPointerEnter"
    @pointerleave="onPointerLeave"
    @focusin="onFocusIn"
    @focusout="onFocusOut"
  >
    <ul ref="trackRef" class="marquee-track flex items-start gap-8 px-8">
      <li
        v-for="chip in category.chips"
        :key="`${chip.prompt}-${chip.copyIndex}`"
        :aria-hidden="chip.isDuplicate ? 'true' : undefined"
        class="shrink-0"
        :class="{ 'fade-in-chip': !chip.isDuplicate }"
        :style="
          chip.isDuplicate
            ? undefined
            : {
                animationDelay: `${chipRevealDelayMs(chip.index, rowIndex, totalRows)}ms`,
              }
        "
      >
        <RevChip
          :label="chip.prompt"
          size="medium"
          @click.prevent="$emit('click', chip.prompt)"
        />
      </li>
    </ul>
  </div>
  <!-- eslint-enable tailwindcss/no-custom-classname -->
</template>

<script lang="ts" setup>
import { ref } from 'vue'

import { RevChip } from '@ds/components/Chip'

import { SCROLL_CONTAINER_ATTRIBUTE } from '../../../../../../constants'
import { useMarqueeDrag } from '../../composables/useMarqueeDrag'
import { useMarqueeDuration } from '../../composables/useMarqueeDuration'
import type { MarqueeChip } from '../../types'

const props = defineProps<{
  category: { prompts: string[]; title: string; chips: MarqueeChip[] }
  rowIndex: number
  totalRows: number
}>()

defineEmits<{ click: [suggestion: string] }>()

// Reveal timing kept in sync with SuggestionModeMarquee's row stagger —
// both live in this folder.
// Delay before the very first element starts revealing, so the reveal
// doesn't start while the incoming page is still settling.
const INITIAL_DELAY_MS = 200
// Gap between consecutive rows — rows reveal bottom-to-top (last row first).
const ROW_STAGGER_MS = 160
// Extra gap between consecutive chips within a single row.
const CHIP_STAGGER_MS = 70

// Animation delay of a single chip: its row's delay plus per-chip stagger.
function chipRevealDelayMs(
  chipIndex: number,
  rowIndex: number,
  totalRows: number,
): number {
  return (
    INITIAL_DELAY_MS +
    (totalRows - 1 - rowIndex) * ROW_STAGGER_MS +
    chipIndex * CHIP_STAGGER_MS
  )
}

const rowRef = ref<HTMLElement | null>(null)
const trackRef = ref<HTMLElement | null>(null)

// Scroll speed of each row in px/s, indexed by row position — the first
// row is always the fastest, whatever the viewport or the number of
// visible rows. The duration is derived from the real track width so
// these speeds are exact.
const ROW_SPEEDS_PX_PER_SEC = [32, 24, 16]

const speedPxPerSec =
  ROW_SPEEDS_PX_PER_SEC[
    Math.min(props.rowIndex, ROW_SPEEDS_PX_PER_SEC.length - 1)
  ]

const { durationMs } = useMarqueeDuration({ trackRef, speedPxPerSec })

const {
  isDragging,
  onPointerDown,
  onPointerEnter,
  onPointerLeave,
  onFocusIn,
  onFocusOut,
} = useMarqueeDrag({
  rowRef,
  trackRef,
  duration: durationMs,
})
</script>

<style scoped>
@keyframes fade-in-chip {
  from {
    opacity: 0;
    transform: translateY(3px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.fade-in-chip {
  animation: 220ms ease-out both fade-in-chip;
}

@keyframes marquee-scroll {
  from {
    transform: translateX(0);
  }
  to {
    transform: translateX(-50%);
  }
}

.marquee-track {
  width: max-content;
  animation: marquee-scroll var(--marquee-duration, 30s) linear infinite;
}

.marquee-row {
  touch-action: pan-y;
  cursor: grab;
}

.marquee-row.is-dragging {
  cursor: grabbing;
}

@media (prefers-reduced-motion: reduce) {
  .marquee-track {
    animation: none;
  }

  .marquee-row {
    overflow-x: auto;
    scroll-snap-type: x mandatory;
    scroll-padding-left: 8px;
  }

  .fade-in-chip {
    animation: none;
    scroll-snap-align: start;
  }
}

/*
 * Gradient mask on the marquee edges — only when the container is wide
 * enough that visible space exists around the marquee (e.g. the desktop
 * web sidebar or full-page mode). On mobile the marquee bleeds to the
 * true screen edge, so no mask is needed. The container context is set
 * on InputWithSuggestions' root via container-type: inline-size.
 */
@container (width > 420px) {
  .marquee-row {
    -webkit-mask-image: linear-gradient(
      to right,
      transparent 0,
      black 32px,
      black calc(100% - 32px),
      transparent 100%
    );
    mask-image: linear-gradient(
      to right,
      transparent 0,
      black 32px,
      black calc(100% - 32px),
      transparent 100%
    );
  }
}
</style>
